<template>
  <div class="discount-code">
    <form
      class="discount-code__form form-group"
      :class="{
        'has-success': codeState === 'valid',
        'has-error': codeState === 'invalid',
      }"
      @submit.prevent="handleSubmit"
    >
      <div class="discount-code__input-container">
        <label
          class="form-group__label visually-hidden"
          for="discount-code-input"
          v-text="$string('cart.code.input_title')"
        />

        <transition name="fade">
          <icon-check-fill
            v-if="codeState === 'valid'"
            class="discount-code__state-icon"
          />
        </transition>

        <input
          id="discount-code-input"
          ref="input"
          v-model.trim="localCode"
          class="discount-code__input"
          autocapitalize="off"
          autocorrect="off"
          name="code"
          :placeholder="$string('cart.code.input_title')"
          type="text"
        />

        <transition name="fade">
          <button
            v-if="localCode?.length"
            class="discount-code__clear"
            :disabled="codeState === 'loading'"
            type="button"
            @click="handleClear"
          >
            <span
              class="visually-hidden"
              v-text="$string('accessibility.clear')"
            />
            <icon-close />
          </button>
        </transition>
      </div>

      <btn
        class="discount-code__submit"
        :label="$string('cart.code.button_title')"
        modifiers="secondary"
        name="discount-code-submit"
        :show-state-icon="codeState === 'loading'"
        type="submit"
      >
        <template #stateIcon>
          <icon-loading />
        </template>
      </btn>
    </form>

    <p
      v-if="errorMessage"
      class="discount-code__error text-mobile-paragraph-xs-regular text-desktop-paragraph-xs-regular"
      v-text="errorMessage"
    />
  </div>
</template>

<script>
/**
 * Vue: Discount code (cart-drawer)
 * -----------------------------------------------------------------------------
 * Discount code input.
 *
 * @param {Boolean} isActive - Cart drawer overlay is active.
 *
 */
import { mapActions, mapState } from 'pinia';

import Btn from '~global/btn/btn';

import IconCheckFill from '~icons/general/check-fill.svg';
import IconClose from '~icons/directional-navigation/close.svg';
import IconLoading from '~icons/general/loading.svg';
import useDiscountCampaignsStore from '~stores/discount-campaigns';

export default {
  name: 'DiscountCode',

  components: {
    Btn,
    IconCheckFill,
    IconClose,
    IconLoading,
  },

  props: {
    isActive: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      localCode: '',
    };
  },

  computed: {
    ...mapState(useDiscountCampaignsStore, {
      code: 'code',
      codeState: 'codeState',
      errorType: 'errorType',
    }),

    /**
     * Compute error message.
     * @returns {String}
     */
    errorMessage() {
      if (this.codeState !== 'invalid' || !this.errorType) {
        return '';
      }

      switch (this.errorType) {
        case 'expired':
          return this.$string('cart.code.errors.expired');
        case 'invalid':
          return this.$string('cart.code.errors.invalid');
        case 'market':
          return this.$string('cart.code.errors.market');
        case 'internal':
        default:
          return this.$string('cart.code.errors.internal');
      }
    },
  },

  watch: {
    /**
     * Watch is active to clear invalid queries.
     */
    isActive() {
      if (!this.isActive) {
        this.onDrawerClose();
      }

      if (this.isActive || this.codeState !== 'invalid') {
        this.onDrawerClose();
        return;
      }

      this.setCode('');
      this.setCodeState('ready');
      this.setErrorType('');
    },

    /**
     * Watch code to update local code.
     * - Used to set uppercase when submitting code for query.
     * - Also sets input if discount code is set elsewhere.
     */
    code: {
      handler() {
        this.localCode = this.code;
      },
      immediate: true,
    },
  },

  methods: {
    ...mapActions(useDiscountCampaignsStore, {
      setCode: 'setCode',
      setCodeState: 'setCodeState',
      setErrorType: 'setErrorType',
      setCartItemPrices: 'setCartItemPrices',
    }),

    /**
     * Handle clear button click.
     */
    handleClear() {
      this.localCode = '';
      this.$refs.input.focus();
      this.setCode('');
      this.setCartItemPrices([{}]);
    },

    /** .
     * Reset code to current code if there is one.
     */
    onDrawerClose() {
      if (!this.code) {
        return;
      }

      this.localCode = this.code;
    },

    /**
     * Handle form submit.
     */
    handleSubmit() {
      this.setCode(this.localCode?.toUpperCase());
    },
  },
};
</script>

<style lang="scss">
@import './discount-code';
</style>
